import { useState, useMemo } from 'react';
import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useCollectionBinding, useSlotGroup, EditableSlotGroup, RichTextContent, useEditMode } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, CollectionBindingDefinition, SlotGroupDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath } from './propertyUtils';
import { PhotoLightbox } from './PhotoLightbox';
import { useFilteredGalleryIndices } from './useFilteredGallery';

const AMENITY_COLLAPSE_THRESHOLD = 15;

function photoSlotDef(index: number): SlotGroupDefinition {
  return {
    label: index === 0 ? 'Hero Photo' : `Grid Photo ${index}`,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const PHOTO_SLOT_DEFS: Record<string, SlotGroupDefinition> = {};
for (let i = 0; i < 5; i++) {
  PHOTO_SLOT_DEFS[`propertyamenities.photo_${i}`] = photoSlotDef(i);
}

/**
 * Links specific amenity names to partner sites.
 */
import flexLogo from '../../assets/images/flex_logo.webp';
import coxLogo from '../../assets/images/cox_logo.webp';
import spectrumLogo from '../../assets/images/spectrum_logo.webp';

const AMENITY_LINKS: { pattern: RegExp; href: string; logo: string; logoAlt: string }[] = [
  { pattern: /flex/i, href: 'https://getflex.com/', logo: flexLogo, logoAlt: 'Flex' },
  { pattern: /cox/i, href: 'https://www.cox.com/quickconnect', logo: coxLogo, logoAlt: 'Cox' },
  { pattern: /spectrum/i, href: 'https://spectrum.com/quickconnect', logo: spectrumLogo, logoAlt: 'Spectrum' },
];

function renderAmenityText(item: string) {
  const link = AMENITY_LINKS.find((l) => l.pattern.test(item));
  if (link) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="prop-amenity-link">
        {item}
        <img src={link.logo} alt={link.logoAlt} className="prop-al-amenity-logo" />
      </a>
    );
  }
  return item;
}

/**
 * Placeholder rendered inside an empty photo slot while in edit mode, so the
 * slot still has a clickable DOM target the inline editor can attach to
 * (an empty slot would otherwise render nothing and be impossible to select).
 */
function PhotoPlaceholder({ label }: { label: string }) {
  return (
    <div
      className="prop-amenities-photo-placeholder"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
        height: '100%',
        minHeight: 120,
        aspectRatio: '1 / 1',
        border: '2px dashed rgba(0, 0, 0, 0.2)',
        borderRadius: 8,
        color: 'rgba(0, 0, 0, 0.45)',
        background: 'rgba(0, 0, 0, 0.03)',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'center',
      }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
      <span>Add {label}</span>
    </div>
  );
}

/**
 * Amenities preview section with photo grid and community/apartment amenity cards.
 * Matches the Stratman amenities-trio-column pattern.
 */
export function PropertyAmenitiesPreview() {
  const property = useProperty();
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;
  const communityAmenitiesCollection = useCollectionBinding<any>('propertyamenities.community_items', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentAmenitiesCollection = useCollectionBinding<any>('propertyamenities.apartment_items', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const basePath = getPropertyBasePath(property);
  const city = property.property_address_city || '';
  const state = property.property_address_state || '';
  const defaultTitle = city && state
    ? `Apartment Features & Amenities at Our Luxurious Apartment Floor Plans in ${city}, ${state}`
    : 'Amenities';
  const sectionTitle = useContentToken('propertyamenities.title') || defaultTitle;
  const communityTitle = useContentToken('propertyamenities.community_title');
  const apartmentTitle = useContentToken('propertyamenities.apartment_title');
  const ctaText = useContentToken('propertyamenities.cta_text');
  const galleryCta = useContentToken('propertyamenities.gallery_cta_text');
  const showMoreText = useContentToken('propertyamenities.show_more_text');
  const showLessText = useContentToken('propertyamenities.show_less_text');

  const communityAmenities = communityAmenitiesCollection.length > 0
    ? communityAmenitiesCollection.map((a: any) => a.title).filter(Boolean)
    : parseAmenityList(property.property_community_amenities_list);
  const apartmentAmenities = apartmentAmenitiesCollection.length > 0
    ? apartmentAmenitiesCollection.map((a: any) => a.title).filter(Boolean)
    : parseAmenityList(property.property_apartment_features_list);

  const fi = useFilteredGalleryIndices();
  const photo0 = useSlotGroup('propertyamenities.photo_0', photoSlotDef(fi[0] ?? 0));
  const photo1 = useSlotGroup('propertyamenities.photo_1', photoSlotDef(fi[1] ?? 1));
  const photo2 = useSlotGroup('propertyamenities.photo_2', photoSlotDef(fi[2] ?? 2));
  const photo3 = useSlotGroup('propertyamenities.photo_3', photoSlotDef(fi[3] ?? 3));
  const photo4 = useSlotGroup('propertyamenities.photo_4', photoSlotDef(fi[4] ?? 4));
  const photoSlots = [photo0, photo1, photo2, photo3, photo4];
  const allPhotos = useMemo(
    () => photoSlots.filter((s) => s.image).map((s) => ({ src: s.image!, alt: s.alt || '' })),
    [photo0.image, photo1.image, photo2.image, photo3.image, photo4.image]
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [communityExpanded, setCommunityExpanded] = useState(false);
  const [apartmentExpanded, setApartmentExpanded] = useState(false);

  const openLightbox = (slotIdx: number) => {
    const visibleIdx = photoSlots
      .slice(0, slotIdx + 1)
      .filter((s) => s.image)
      .length - 1;
    if (visibleIdx >= 0) {
      setLightboxIndex(visibleIdx);
      setLightboxOpen(true);
    }
  };

  return (
    <section className="prop-amenities">
      <div className="prop-container">
        <div className="prop-amenities-heading">
          <EditableToken path="propertyamenities.title" type="text" label="Section Title" as="h2" className="prop-box-separator" defaultValue={contentSchema.tokens['propertyamenities.title'].defaultValue}>
            {sectionTitle}
          </EditableToken>
          <RichTextContent
            path="propertyamenities.blurb"
            label="Description"
            defaultField={contentSchema.tokens['propertyamenities.blurb'].defaultField}
            defaultValue={contentSchema.tokens['propertyamenities.blurb'].defaultValue}
            as="div"
          />
        </div>

        <div className="prop-amenities-grid">
          {/* Photo column: hero + 2x2 grid (slot groups for individually swappable images) */}
          <div className="prop-amenities-photos">
            {(photo0.image || isEditMode) && (
              <EditableSlotGroup groupPath="propertyamenities.photo_0" definition={PHOTO_SLOT_DEFS['propertyamenities.photo_0']}>
                {photo0.image ? (
                  <button
                    type="button"
                    className="prop-amenities-photo-hero"
                    onClick={() => openLightbox(0)}
                  >
                    <VibesitesImage url={photo0.image} alt={photo0.alt || ''} sizes="(min-width: 1200px) 33vw, 100vw" />
                  </button>
                ) : (
                  <div className="prop-amenities-photo-hero">
                    <PhotoPlaceholder label="Hero Photo" />
                  </div>
                )}
              </EditableSlotGroup>
            )}
            {(photo1.image || photo2.image || photo3.image || photo4.image || isEditMode) && (
              <div className="prop-amenities-photo-grid">
                {(photo1.image || isEditMode) && (
                  <EditableSlotGroup groupPath="propertyamenities.photo_1" definition={PHOTO_SLOT_DEFS['propertyamenities.photo_1']}>
                    {photo1.image ? (
                      <button type="button" aria-label={photo1.alt || 'View amenity photo 1'} onClick={() => openLightbox(1)}>
                        <VibesitesImage url={photo1.image} alt={photo1.alt || ''} sizes="(min-width: 1200px) 17vw, (min-width: 768px) 50vw, 100vw" />
                      </button>
                    ) : (
                      <PhotoPlaceholder label="Grid Photo 1" />
                    )}
                  </EditableSlotGroup>
                )}
                {(photo2.image || isEditMode) && (
                  <EditableSlotGroup groupPath="propertyamenities.photo_2" definition={PHOTO_SLOT_DEFS['propertyamenities.photo_2']}>
                    {photo2.image ? (
                      <button type="button" aria-label={photo2.alt || 'View amenity photo 2'} onClick={() => openLightbox(2)}>
                        <VibesitesImage url={photo2.image} alt={photo2.alt || ''} sizes="(min-width: 1200px) 17vw, (min-width: 768px) 50vw, 100vw" />
                      </button>
                    ) : (
                      <PhotoPlaceholder label="Grid Photo 2" />
                    )}
                  </EditableSlotGroup>
                )}
                {(photo3.image || isEditMode) && (
                  <EditableSlotGroup groupPath="propertyamenities.photo_3" definition={PHOTO_SLOT_DEFS['propertyamenities.photo_3']}>
                    {photo3.image ? (
                      <button type="button" aria-label={photo3.alt || 'View amenity photo 3'} onClick={() => openLightbox(3)}>
                        <VibesitesImage url={photo3.image} alt={photo3.alt || ''} sizes="(min-width: 1200px) 17vw, (min-width: 768px) 50vw, 100vw" />
                      </button>
                    ) : (
                      <PhotoPlaceholder label="Grid Photo 3" />
                    )}
                  </EditableSlotGroup>
                )}
                {(photo4.image || isEditMode) && (
                  <EditableSlotGroup groupPath="propertyamenities.photo_4" definition={PHOTO_SLOT_DEFS['propertyamenities.photo_4']}>
                    {photo4.image ? (
                      <button type="button" aria-label={photo4.alt || 'View amenity photo 4'} onClick={() => openLightbox(4)}>
                        <VibesitesImage url={photo4.image} alt={photo4.alt || ''} sizes="(min-width: 1200px) 17vw, (min-width: 768px) 50vw, 100vw" />
                      </button>
                    ) : (
                      <PhotoPlaceholder label="Grid Photo 4" />
                    )}
                  </EditableSlotGroup>
                )}
              </div>
            )}
            <div className="prop-amenities-photo-ctas">
              <EditableToken path="propertyamenities.cta_text" type="text" label="CTA Button Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyamenities.cta_text'].defaultValue}>
                <a className="prop-btn" href={`${basePath}/amenities`}>{ctaText}</a>
              </EditableToken>
              <EditableToken path="propertyamenities.gallery_cta_text" type="text" label="Gallery CTA Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyamenities.gallery_cta_text'].defaultValue}>
                <a className="prop-btn prop-btn-border" href={`${basePath}/photos`}>{galleryCta}</a>
              </EditableToken>
            </div>
          </div>

          {/* Community amenities card */}
          <div>
            <div className="prop-amenity-card">
              <div className="prop-amenity-card-icon">
                <svg viewBox="0 0 48.193 37.494" width="48" height="38" fill="currentColor">
                  <path d="M10.768,40.009a8.01,8.01,0,0,1,15.959-.987l.084.686a2.677,2.677,0,1,1-5.314.661l-.084-.686a2.655,2.655,0,0,0-5.289.326v8.059H32.191V40.009a8.01,8.01,0,0,1,15.959-.987l.084.686a2.677,2.677,0,1,1-5.314.661l-.084-.686a2.655,2.655,0,0,0-5.289.326V59.532c-.134.084-.276.167-.4.259a9.218,9.218,0,0,1-4.954,1.7h0V53.423H16.124V61.5a9.192,9.192,0,0,1-4.946-1.7,4.218,4.218,0,0,0-.41-.259V40.009ZM25.706,61.95a11.71,11.71,0,0,0,6.485,2.184,11.838,11.838,0,0,0,6.477-2.184h0a2.666,2.666,0,0,1,3.28.142A10.569,10.569,0,0,0,46.183,64.2a2.676,2.676,0,1,1-1.213,5.214,15.927,15.927,0,0,1-4.87-2.092,16.885,16.885,0,0,1-7.908,2.167,16.558,16.558,0,0,1-6.728-1.582c-.485-.226-.929-.444-1.305-.644-.377.2-.812.427-1.305.644a16.558,16.558,0,0,1-6.728,1.582,16.974,16.974,0,0,1-7.908-2.159,16.23,16.23,0,0,1-4.87,2.092A2.676,2.676,0,0,1,2.132,64.21,10.569,10.569,0,0,0,6.366,62.1a2.69,2.69,0,0,1,3.28-.142h0a11.919,11.919,0,0,0,6.477,2.176,11.71,11.71,0,0,0,6.485-2.184A2.665,2.665,0,0,1,25.706,61.95Z" transform="translate(-0.062 -32)"/>
                </svg>
              </div>
              <EditableToken path="propertyamenities.community_title" type="text" label="Community Amenities Title" as="h2" className="prop-box-separator" defaultValue={contentSchema.tokens['propertyamenities.community_title'].defaultValue}>
                {communityTitle}
              </EditableToken>
              <ul>
                {communityAmenities.length > 0 ? (
                  <>
                    {(communityExpanded ? communityAmenities : communityAmenities.slice(0, AMENITY_COLLAPSE_THRESHOLD)).map((a, i) => <li key={i}>{renderAmenityText(a)}</li>)}
                    {communityAmenities.length > AMENITY_COLLAPSE_THRESHOLD && (
                      <li className="prop-amenity-toggle-wrap">
                        <button
                          type="button"
                          className="prop-amenity-toggle"
                          onClick={() => setCommunityExpanded((e) => !e)}
                          aria-expanded={communityExpanded}
                        >
                          {communityExpanded ? (
                            <EditableToken path="propertyamenities.show_less_text" type="text" label="Show Less Button Text" as="span" defaultValue={contentSchema.tokens['propertyamenities.show_less_text'].defaultValue}>
                              {showLessText}
                            </EditableToken>
                          ) : (
                            <EditableToken path="propertyamenities.show_more_text" type="text" label="Show More Button Text" as="span" defaultValue={contentSchema.tokens['propertyamenities.show_more_text'].defaultValue}>
                              {showMoreText}
                            </EditableToken>
                          )}
                        </button>
                      </li>
                    )}
                  </>
                ) : (
                  <li>Contact us for amenity details</li>
                )}
              </ul>
            </div>
          </div>

          {/* Apartment amenities card */}
          <div>
            <div className="prop-amenity-card dark">
              <div className="prop-amenity-card-icon">
                <svg viewBox="0 0 49.49 39.6" width="48" height="38" fill="currentColor">
                  <path d="M0,2.475V4.949A2.472,2.472,0,0,0,2.475,7.424H47.016A2.472,2.472,0,0,0,49.49,4.949V2.475A2.472,2.472,0,0,0,47.016,0H2.475A2.472,2.472,0,0,0,0,2.475ZM47.016,9.9H2.475v27.22a2.472,2.472,0,0,0,2.475,2.475h7.424V27.22a12.373,12.373,0,1,1,24.745,0V39.592h7.424a2.472,2.472,0,0,0,2.475-2.475ZM23.245,20A.826.826,0,0,0,22.7,19.8a.866.866,0,0,0-.564.209,24.9,24.9,0,0,0-4.578,5.39,10.89,10.89,0,0,0-1.825,5.212,9,9,0,0,0,18,0,10.978,10.978,0,0,0-1.361-4.439,16.787,16.787,0,0,0-3.395-4.562.831.831,0,0,0-1.121,0,14.275,14.275,0,0,0-1.485,1.663,31.837,31.837,0,0,0-3.124-3.248Zm4.3,15.141a4.8,4.8,0,0,1-2.753.843A4.266,4.266,0,0,1,20.237,31.6c0-1.353.843-2.529,2.529-4.609.224.278,3.488,4.446,3.488,4.446l2.026-2.366a4.347,4.347,0,0,1,.394.673,4.166,4.166,0,0,1-1.121,5.4Z"/>
                </svg>
              </div>
              <EditableToken path="propertyamenities.apartment_title" type="text" label="Apartment Amenities Title" as="h2" className="prop-box-separator" defaultValue={contentSchema.tokens['propertyamenities.apartment_title'].defaultValue}>
                {apartmentTitle}
              </EditableToken>
              <ul>
                {apartmentAmenities.length > 0 ? (
                  <>
                    {(apartmentExpanded ? apartmentAmenities : apartmentAmenities.slice(0, AMENITY_COLLAPSE_THRESHOLD)).map((a, i) => <li key={i}>{renderAmenityText(a)}</li>)}
                    {apartmentAmenities.length > AMENITY_COLLAPSE_THRESHOLD && (
                      <li className="prop-amenity-toggle-wrap">
                        <button
                          type="button"
                          className="prop-amenity-toggle"
                          onClick={() => setApartmentExpanded((e) => !e)}
                          aria-expanded={apartmentExpanded}
                        >
                          {apartmentExpanded ? (
                            <EditableToken path="propertyamenities.show_less_text" type="text" label="Show Less Button Text" as="span" defaultValue={contentSchema.tokens['propertyamenities.show_less_text'].defaultValue}>
                              {showLessText}
                            </EditableToken>
                          ) : (
                            <EditableToken path="propertyamenities.show_more_text" type="text" label="Show More Button Text" as="span" defaultValue={contentSchema.tokens['propertyamenities.show_more_text'].defaultValue}>
                              {showMoreText}
                            </EditableToken>
                          )}
                        </button>
                      </li>
                    )}
                  </>
                ) : (
                  <li>Contact us for amenity details</li>
                )}
              </ul>
              {apartmentAmenities.some(a => a.includes('*')) && (
                <div className="prop-amenity-disclaimer"><p>*In Select Homes</p></div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PhotoLightbox
        photos={allPhotos}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </section>
  );
}

function parseAmenityList(list: string | null | undefined): string[] {
  if (!list) return [];
  return list.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyAmenities',
  displayName: 'Property Amenities Preview',
  tokens: {
    'propertyamenities.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Amenities',
    },
    'propertyamenities.blurb': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue: 'When you live at <span data-type="field-token" data-field="property_name"></span>, not only will you get top-of-the-line apartment features, but you will also have access to outstanding community amenities.',
    },
    'propertyamenities.community_title': {
      label: 'Community Amenities Title',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'propertyamenities.apartment_title': {
      label: 'Apartment Amenities Title',
      type: 'text',
      defaultValue: 'Apartment Amenities',
    },
    'propertyamenities.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      defaultValue: 'View All Amenities',
    },
    'propertyamenities.gallery_cta_text': {
      label: 'Gallery CTA Text',
      type: 'text',
      defaultValue: 'Our Photo Gallery',
    },
    'propertyamenities.show_more_text': {
      label: 'Show More Button Text',
      type: 'text',
      defaultValue: 'Show more',
    },
    'propertyamenities.show_less_text': {
      label: 'Show Less Button Text',
      type: 'text',
      defaultValue: 'Show less',
    },
  },
  slotGroups: PHOTO_SLOT_DEFS,
  collectionBindings: {
    'propertyamenities.community_items': {
      label: 'Community Amenities',
      collection: 'amenities',
      description: 'Community amenity items',
      filter: { group: 'Community' },
    } as CollectionBindingDefinition,
    'propertyamenities.apartment_items': {
      label: 'Apartment Amenities',
      collection: 'amenities',
      description: 'Apartment amenity items',
      filter: { group: 'Apartment' },
    } as CollectionBindingDefinition,
  },
};

(PropertyAmenitiesPreview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyAmenitiesPreview;
